/*
 * Copyright © 2026, Commonwealth Scientific and Industrial Research Organisation
 * (CSIRO) ABN 41 687 119 230. All rights reserved
 *
 * Author: Derek Ireland
 */
import { useState } from "react";
import { Tabs, Tab } from "react-bootstrap";

import { ACTIVE_PATIENTS, ARCHIVED_PATIENTS } from "../../util/const";
import { ARCHIVED_TAB_KEY, hasOpenedArchivedTab } from "../../util/patientListKey";
import { useActivePatients, useArchivedPatients } from "../../services/useData";
import { Patient } from "../../types/patient";
import ActivePatients from "../ActivePatients";
import ArchivedPatients from "../ArchivedPatients";


export type HomePageProps = {
  toggleDisplay: (didSave: boolean) => void;
};

export type PatientProps = {
  patients: Patient[];
  // True while the list is being fetched for the first time. Only the archived
  // list loads after render; the active list suspends instead.
  isLoading?: boolean;
  refreshData: (isArchivingOrUnarchiving: boolean) => Promise<Patient[]>;
};

const PatientList = () => {

  const activeData = useActivePatients();

  // The archived list is only requested once the clinician opens its tab, and
  // stays subscribed from then on so archiving continues to refresh it.
  const [archivedOpened, setArchivedOpened] = useState(false);
  const archivedData = useArchivedPatients(archivedOpened);

  const refreshData = async (isArchivingOrUnarchiving: boolean): Promise<Patient[]> => {

    // A no-op until the archived list has been requested at least once.
    if(isArchivingOrUnarchiving)
      archivedData.mutate();

    const patients = await activeData.mutate();
    return patients ?? [];
  };

  return (
    <Tabs
      defaultActiveKey="active"
      id="patient-tab"
      className="mb-3"
      onSelect={(k) => {
        setArchivedOpened((opened) => hasOpenedArchivedTab(opened, k));
      }}>
      <Tab eventKey="active" title={ACTIVE_PATIENTS}>
        <ActivePatients patients={activeData.patients} refreshData={refreshData} />
      </Tab>
      <Tab eventKey={ARCHIVED_TAB_KEY} title={ARCHIVED_PATIENTS}>
        <ArchivedPatients patients={archivedData.patients} isLoading={archivedData.isLoading} refreshData={refreshData} />
      </Tab>
    </Tabs>
  );
};

export default PatientList;
